import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {
  createBonusQuestion,
  fetchBonusQuestion,
  updateBonusQuestion,
} from '../api';
import Button from '../components/Button';
import ConfirmModal from '../components/ConfirmModal';
import {
  CHAMPIONSHIP_OPTIONS,
  INITIAL_DIFFUSION_VALUES,
  INITIAL_QUESTION_VALUES,
  QUESTION_BONUS_LIST_PATH,
  createInitialAnswersValues,
} from '../constants';
import {
  buildBonusQuestionPayload,
  buildBonusQuestionUpdatePayload,
  isBonusQuestionComplete,
  toFormValues,
} from '../payload';
import {questionBonusStyles, translationsStyles} from '../styles';
import {
  AnswersValues,
  BonusQuestionFormMode,
  BonusQuestionSubmitStatus,
  BonusQuestionType,
  DiffusionValues,
  QuestionTitleValues,
  TranslationLanguage,
} from '../types';
import AnswersSection from './AnswersSection';
import DiffusionSection from './DiffusionSection';
import QuestionSection from './QuestionSection';
import TranslationsModal from './TranslationsModal';

const PAGE_TITLES: Record<BonusQuestionFormMode, string> = {
  create: 'Create Question Bonus',
  edit: 'Edit Question Bonus',
  view: 'Question Bonus',
};

interface QuestionBonusFormProps {
  history?: {push: (path: string) => void};
  match?: {path: string; params: {bonusQuestionId?: string}};
}

/** Le mode découle de la route : /create, /edit/:id ou /view/:id. */
const getMode = (path?: string): BonusQuestionFormMode => {
  if (path?.includes('/view/')) return 'view';
  if (path?.includes('/edit/')) return 'edit';
  return 'create';
};

export const QuestionBonusForm = ({history, match}: QuestionBonusFormProps) => {
  const dispatch = useDispatch();

  const mode = getMode(match?.path);
  const bonusQuestionId = match?.params?.bonusQuestionId;
  const isReadOnly = mode === 'view';

  const [diffusion, setDiffusion] = useState<DiffusionValues>(
    INITIAL_DIFFUSION_VALUES,
  );
  const [questionType, setQuestionType] = useState<BonusQuestionType>('quiz');
  const [questionTitle, setQuestionTitle] = useState<QuestionTitleValues>(
    INITIAL_QUESTION_VALUES,
  );
  const [answers, setAnswers] = useState<AnswersValues>(
    createInitialAnswersValues(),
  );

  const [isLoading, setIsLoading] = useState(mode !== 'create');
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [isConfirmingSchedule, setIsConfirmingSchedule] = useState(false);
  const [isTranslationsModalOpen, setIsTranslationsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!bonusQuestionId) return;

    setIsLoading(true);
    dispatch(
      fetchBonusQuestion(
        bonusQuestionId,
        bonusQuestion => {
          const values = toFormValues(bonusQuestion);
          setDiffusion(values.diffusion);
          setQuestionType(values.questionType);
          setQuestionTitle(values.questionTitle);
          setAnswers(values.answers);
          setIsLoading(false);
        },
        () => {
          setLoadError('Le chargement de la question a échoué.');
          setIsLoading(false);
        },
      ),
    );
  }, [dispatch, bonusQuestionId]);

  /** Le formulaire ne saisit que le FR ; EN/ES passent par la modale. */
  const updateQuestionTitle = (title: string) => {
    setQuestionTitle(currentTitle => ({...currentTitle, fr: title}));
  };

  const updateTitleTranslation = (
    language: TranslationLanguage,
    value: string,
  ) => {
    setQuestionTitle(currentTitle => ({...currentTitle, [language]: value}));
  };

  const updateQuizOptionTranslation = (
    optionKey: string,
    language: TranslationLanguage,
    value: string,
  ) => {
    setAnswers(currentAnswers => ({
      ...currentAnswers,
      quiz: {
        options: currentAnswers.quiz.options.map(option =>
          option.key === optionKey
            ? {...option, label: {...option.label, [language]: value}}
            : option,
        ),
      },
    }));
  };

  const [translationsSnapshot, setTranslationsSnapshot] = useState<
    | {
        title: QuestionTitleValues;
        optionLabels: Record<string, QuestionTitleValues>;
      }
    | undefined
  >(undefined);

  const openTranslationsModal = () => {
    setTranslationsSnapshot({
      title: questionTitle,
      optionLabels: answers.quiz.options.reduce(
        (labels: Record<string, QuestionTitleValues>, option) => ({
          ...labels,
          [option.key]: option.label,
        }),
        {},
      ),
    });
    setIsTranslationsModalOpen(true);
  };

  const closeTranslationsModal = () => {
    setTranslationsSnapshot(undefined);
    setIsTranslationsModalOpen(false);
  };

  const cancelTranslations = () => {
    if (translationsSnapshot) {
      const {title, optionLabels} = translationsSnapshot;
      setQuestionTitle(title);
      setAnswers(currentAnswers => ({
        ...currentAnswers,
        quiz: {
          options: currentAnswers.quiz.options.map(option => ({
            ...option,
            label: optionLabels[option.key] || option.label,
          })),
        },
      }));
    }
    closeTranslationsModal();
  };

  const formValues = {diffusion, questionType, questionTitle, answers};
  const canSchedule = isBonusQuestionComplete(formValues);

  const championshipLabel = CHAMPIONSHIP_OPTIONS.find(
    option => option.value === diffusion.championshipId,
  )?.label;

  /** Le match pronostiqué dépend du championnat et de la journée : il se réinitialise avec eux. */
  const onDiffusionChange = (nextDiffusion: DiffusionValues) => {
    const hasScopeChanged =
      nextDiffusion.championshipId !== diffusion.championshipId ||
      nextDiffusion.gameWeekNumber !== diffusion.gameWeekNumber;

    if (hasScopeChanged) {
      setAnswers(currentAnswers => ({
        ...currentAnswers,
        matchForecast: {
          ...currentAnswers.matchForecast,
          championshipMatchId: undefined,
        },
      }));
    }
    setDiffusion(nextDiffusion);
  };

  const submit = (status: BonusQuestionSubmitStatus) => {
    setIsSubmitting(true);
    setSubmitError(undefined);

    const isUpdate = mode === 'edit' && Boolean(bonusQuestionId);

    const onSuccess = () => {
      setIsSubmitting(false);
      setIsConfirmingSchedule(false);
      if (history) history.push(QUESTION_BONUS_LIST_PATH);
    };
    const onError = (error: unknown) => {
      setIsSubmitting(false);
      setIsConfirmingSchedule(false);
      setSubmitError(
        typeof error === 'string'
          ? error
          : "L'enregistrement de la question a échoué.",
      );
    };

    dispatch(
      isUpdate && bonusQuestionId
        ? updateBonusQuestion(
            bonusQuestionId,
            buildBonusQuestionUpdatePayload(formValues, status),
            onSuccess,
            onError,
          )
        : createBonusQuestion(
            buildBonusQuestionPayload(formValues, status),
            onSuccess,
            onError,
          ),
    );
  };

  if (isLoading) {
    return (
      <div style={questionBonusStyles.page}>
        <p style={questionBonusStyles.emptyState}>Chargement de la question…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={questionBonusStyles.page}>
        <p style={questionBonusStyles.errorMessage}>{loadError}</p>
      </div>
    );
  }

  return (
    <div style={questionBonusStyles.page}>
      <h1 style={questionBonusStyles.pageTitle}>
        {PAGE_TITLES[mode]} {championshipLabel}
        <span style={questionBonusStyles.pageTitleSuffix}>MPP</span>
      </h1>

      <DiffusionSection
        values={diffusion}
        onChange={onDiffusionChange}
        readOnly={isReadOnly}
        // Le back refuse de déplacer une question existante.
        isScopeLocked={mode !== 'create'}
      />
      <QuestionSection
        type={questionType}
        title={questionTitle}
        onChangeType={setQuestionType}
        onChangeTitle={updateQuestionTitle}
        readOnly={isReadOnly}
      />
      <AnswersSection
        questionType={questionType}
        diffusion={diffusion}
        values={answers}
        onChange={setAnswers}
        readOnly={isReadOnly}
      />

      {submitError && (
        <p style={questionBonusStyles.errorMessage}>{submitError}</p>
      )}

      {/* Consultation seule : pas de CTA en bas de page. */}
      {!isReadOnly && (
        <div style={questionBonusStyles.footer}>
          <Button
            disabled={!canSchedule || isSubmitting}
            onClick={() => setIsConfirmingSchedule(true)}>
            Programmer
          </Button>
          {/* Le brouillon enregistre l'état courant, même incomplet, sans validation. */}
          <Button
            variant="secondary"
            disabled={isSubmitting}
            onClick={() => submit('draft')}>
            Enregistrer en brouillon
          </Button>
          <div style={translationsStyles.ctaWithRequired}>
            <Button
              variant="secondarySuccess"
              disabled={isSubmitting}
              onClick={openTranslationsModal}>
              Gérer les traductions
            </Button>
            <span style={translationsStyles.required} aria-hidden="true">
              *
            </span>
          </div>
        </div>
      )}

      {isTranslationsModalOpen && (
        <TranslationsModal
          questionType={questionType}
          title={questionTitle}
          quizOptions={answers.quiz.options}
          onChangeTitle={updateTitleTranslation}
          onChangeQuizOptionLabel={updateQuizOptionTranslation}
          onCancel={cancelTranslations}
          onValidate={closeTranslationsModal}
        />
      )}

      {isConfirmingSchedule && (
        <ConfirmModal
          title="Programmer la question bonus ?"
          description={`La question sera programmée sur la journée ${diffusion.gameWeekNumber} et deviendra visible ${diffusion.visibilityHoursBeforeGameWeekStart}h avant son début.`}
          confirmLabel="Programmer"
          isConfirming={isSubmitting}
          onConfirm={() => submit('scheduled')}
          onCancel={() => setIsConfirmingSchedule(false)}
        />
      )}
    </div>
  );
};

export default QuestionBonusForm;



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/Form/index.tsx